import { Ticket } from '@wix/answers-api';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { FunctionComponent } from 'react';
import 'moment/min/locales';

import { TOOLTIP_ENTER_DELAY } from '../../../../constants';
import { DATA_HOOKS } from '../../../../dataHooks';
import { InnerLink } from '../../../InnerLink';

import css from './index.module.scss';

export type MyTicketsListProps = {
  tickets: Ticket[];
};

export const MyTicketsList: FunctionComponent<MyTicketsListProps> = ({
  tickets,
}) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const formatDate = (data?: number | Date) =>
    moment(data)
      .locale(locale as string)
      .format('LL');

  return (
    <Box direction="vertical" dataHook={DATA_HOOKS.MY_ACTIVITY_TICKETS_LIST}>
      <ul className={css.list}>
        {tickets?.map((ticket) => (
          <li key={ticket.id} className={css.listItem}>
            <InnerLink
              dataHook={DATA_HOOKS.MY_ACTIVITY_TICKETS_ITEM}
              href={ticket.uri}
            >
              <Box direction="horizontal" verticalAlign="middle">
                <Image
                  src={
                    'https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/ticket.svg'
                  }
                  width="29"
                  height="31"
                  alt=""
                  className={css.listIcon}
                  loading="eager"
                />
                <Box
                  overflow="hidden"
                  direction="vertical"
                  verticalAlign="middle"
                  align="space-between"
                >
                  <Text
                    size="medium"
                    ellipsis={true}
                    tooltipProps={{ enterDelay: TOOLTIP_ENTER_DELAY }}
                    dataHook={DATA_HOOKS.MY_ACTIVITY_TICKETS_ITEM_TITLE}
                    className={css.title}
                  >
                    {ticket.subject}
                  </Text>
                  <Box
                    className="info"
                    direction="horizontal"
                    verticalAlign="middle"
                    gap={1}
                  >
                    <Text size="tiny" className={css.creationDate}>
                      {formatDate(ticket.creationDate)}
                    </Text>
                    <div className={css.dotSeparator} />
                    <Box
                      dataHook={DATA_HOOKS.MY_ACTIVITY_TICKET_STATUS}
                      gap={1}
                    >
                      {ticket.repliesCount > 0 ? (
                        <Text size="tiny" className={css.withReplies}>
                          {t('user.my-activity.replies', {
                            count: ticket.repliesCount,
                          })}
                        </Text>
                      ) : (
                        <Text size="tiny" className={css.noReply}>
                          {t('user.my-activity.waiting.for.reply')}
                        </Text>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </InnerLink>
          </li>
        ))}
      </ul>
    </Box>
  );
};
